/* eslint-disable @typescript-eslint/no-unused-vars */
import { QueryClient, UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { getApiKey } from "../services/apiKeyEndpoint";
import { readAllAutomations, readAutomationLogs, readSignalAutomations } from "../services/automation/automationEndpoints";
import { getAccountById } from "../services/companyEndpoint";
import { readAllMetrics } from "../services/metric/metricEndpoint";
import { readAllPlaybooks } from "../services/playbook/playbookEndpoints";
import { readWorkspacePreference } from "../services/preference/preferenceEndpoints";
import { getContactById, getContactsByAccountId } from "../services/userContactsEndpoint";
import { currentUser } from "../services/usersEndpoint";
import { getViews } from "../services/view/viewEndpoints";
import {
  getWorkspace,
  getWorkspaceFieldsAndMetrics,
  getWorkspaceObjectRelationships,
  getWorkspaceObjectsDetailed,
  getWorkspaceObjectWithFields,
  updateWorkspaceObject,
  createWorkspaceObject,
  getWorkspaceFields as getWorkspaceFieldsAPI,
  getObjectFields as getObjectFieldsAPI,
  createObjectField,
  createBulkObjectFields,
  updateWorkspaceField
} from "../services/workspace/workspaceEndpoints";
import { allWorkspaceMembers } from "../services/workspaceMemberEndpoint";
import arrayToObject from "../utils/helpers";
import notify from "../utils/notify";
import { readSignalRuns } from "../services/inbox/inboxEndpoints";
import { getRecommendedSignals, RecommendedSignalsResponse } from "../services/signals/recommendedSignalsEndpoint";
import {
  getAllIntegration, getIntegrationEvents, getSlackChannels, getSlackIntegration, getSlackUsers,
} from "../services/integrationEndpoint";
import { getMemberIntegrationsMe, updateMemberIntegration } from "../services/memberIntegrationsEndpoint";
import {
  getEmailSettings,
  updateEmailSettings,
  getEmailDomainReview,
  allowEmailDomain,
  blockEmailDomain,
} from "../services/emailSettingsEndpoint";
import type {
  EmailSettings,
  EmailDomainReviewList,
  EmailDomainReviewStatus,
  UpdateEmailSettings,
} from "../services/emailSettingsEndpoint";
import { readHealthScoreSettings } from "../services/healthScoreSettings/healthScoreSettingsEndpoints";
import { ExportResponse, ListExportsResponse } from "../services/exports/exportsEndpoints.interface";
import { createExport, downloadExport, getExport, listExports } from "../services/exports/exportsEndpoints";
import { GetWorkspaceMembersResponse } from "../services/workspace/workspaceEndpoints.interface";
import { generateIntegrationAppToken, verifyIntegrationAppToken, testIntegrationAppConnection, getWorkspaceFields, getObjectFields, createBulkFields } from "../services/integrationAppEndpoint";
import {
  getAvailableObjects,
  getFieldsAndMetrics,
  getSyncConfigurations,
  getWorkspaceObjects,
  getObjectFields as getSyncObjectFields,
  getFieldMappings,
  updateSyncConfiguration,
  createSyncConfiguration,
  createWriteBackSync,
  deleteSyncConfiguration,
  createBulkFieldMappings,
  updateFieldMapping,
  deleteFieldMapping
} from "../services/syncConfiguration/syncConfigurationEndpoints";
import {
  listAIMemoryObjects,
  enableAIMemoryObject,
  getAIMemoryFieldDefinitions,
  updateAIMemoryFieldDefinition,
} from "../services/aiMemory/aiMemoryEndpoints";
import type {
  ListAIMemoryObjectsResponse,
  GetFieldDefinitionsResponse,
} from "../services/aiMemory/aiMemoryEndpoints.interface";
import type { GetWorkspaceObjectsResponse } from "../services/syncConfiguration/syncConfigurationEndpoints.interface";
import {
  getAccountAssociatedObjects,
  getAccountEmails,
  getAccountMeetings,
  getContactAssociatedObjects,
  getContactEmails,
  getContactMeetings,
} from "../services/associatedObjectsEndpoint";
import {
  getTraitHistory,
  getTraitHistoryAll,
  TraitHistoryResponse,
  TraitHistoryAllResponse,
} from "../services/computedTraits/traitHistoryEndpoints";
import { listComputedLabels } from "../services/computedTraits/computedTraitsEndpoints";
import type { ComputedLabel } from "../services/computedTraits/computedTraitsEndpoints.interface";
import type { AssociatedObjectsResponse, EmailsResponse, MeetingsResponse } from "../shared/emails-meetings/types";
import { getObjectFieldValues } from "../services/workspace/objectFieldValuesEndpoint";
import {
  getMeetingNoteTakerPreference,
  upsertMeetingNoteTakerPreference,
  deleteMeetingNoteTakerPreference,
  MeetingNoteTakerPreferenceUpdate,
  MeetingNoteTakerPreference,
} from "../services/meetingNoteTakerEndpoint";
import {
  getWorkspaceSkills,
  getSkill,
  createSkill,
  updateSkill,
  deleteSkill,
  toggleSkill,
  getSkillVersions,
  generateSkillContent,
} from "../services/skills/skillsEndpoints";
import type {
  CreateSkillPayload,
  UpdateSkillPayload,
  GenerateSkillContentPayload,
} from "../services/skills/skillsEndpoints.interface";
import {
  getSignalDocument,
  listSignalDocuments,
  getDocumentVersions,
  submitDocumentFeedback,
  listDocumentFeedback,
  getSkillEffectiveness,
} from "../services/signalDocuments/signalDocumentsEndpoints";
import type { SubmitFeedbackPayload } from "../services/signalDocuments/signalDocumentsEndpoints.interface";

export const queryClient = new QueryClient();

export const useCurrentUser = ({ ...queryOptions }: UseQueryOptions) => useQuery({
  queryKey: ["user"],
  queryFn: async () => {
    const response = await currentUser();
    if (!(response.status === 200)) {
      notify({
        heading: "Error",
        messageList: ["Failed to fetch user, kindly try again"],
        type: "failure",
      });
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
    }
    return response.data;
  },
  staleTime: 1000 * 60 * 60 * 24,
  ...queryOptions,
});

export const useApiKey = ({ workspaceId, ...queryOptions }:
  { workspaceId?: string } & UseQueryOptions) => useQuery({
    queryKey: ["apiKey", workspaceId],
    queryFn: () => getApiKey({
      workspace_id: workspaceId,
    }),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 60 * 24,
    ...queryOptions,
  });

export const useGetEvents = ({
  workspaceId, integration_name, token, ...queryOptions
}:
  { workspaceId: string, integration_name: string, token: string } & UseQueryOptions) => useQuery({
    queryKey: ["getEvents", workspaceId, integration_name, token],
    queryFn: () => getIntegrationEvents({
      workspace_id: workspaceId,
      integration_name,
      token,
    }),
    select: (ev: any) => {
      // Check if the data already has the correct structure
      if (ev?.data?.group || ev?.data?.identify || ev?.data?.track) {
        console.log('Events data already structured:', ev?.data);
        return ev?.data;
      }

      // Otherwise, transform the events array
      const events = ev?.data?.events;
      if (!events) {
        console.log('No events found in response:', ev?.data);
        return {};
      }

      const data: any = {};
      events.forEach((event: any) => {
        if (data[event.event_type]) {
          data[event.event_type].push({ ...event });
        } else {
          data[event.event_type] = [{ ...event }];
        }
      });
      console.log('Transformed events data:', data);
      return data;
    },
    ...queryOptions,
  });

export const useCurrentWorkspace = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => getWorkspace({
      workspace_id: workspaceId,
    }),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 60 * 24,
    ...queryOptions,
  });

export const useWorkspacePreferences = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["workspace_preferences", workspaceId],
    queryFn: () => readWorkspacePreference({
      workspace_id: workspaceId,
    }),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 30,
    ...queryOptions,
  });

export const useWorkspaceMetricPreferences = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["workspace_metric_preferences", workspaceId],
    queryFn: () => readAllMetrics({
      workspace_id: workspaceId,
    }),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 30,
    ...queryOptions,
  });

export const useWorkspaceMembers = ({ workspaceId, ...queryOptions }: { workspaceId: string }
  & UseQueryOptions<GetWorkspaceMembersResponse, Error>) => useQuery<GetWorkspaceMembersResponse, Error>({
    queryKey: ["workspace_members", workspaceId],
    queryFn: () => allWorkspaceMembers({ workspace_id: workspaceId }).then((res) => res.data),
    staleTime: 1000 * 60 * 60 * 24,
    ...queryOptions,
  });

export const useMemberIntegrationsMe = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["member-integrations", workspaceId, "me"],
    queryFn: () => getMemberIntegrationsMe({ workspace_id: workspaceId }),
    select: (data: any) => data?.data,
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useUpdateMemberIntegration = () => useMutation({
  mutationFn: ({
    workspace_id,
    id,
    is_active,
    is_default_for_member,
    options,
  }: {
    workspace_id: string;
    id: string;
    is_active?: boolean;
    is_default_for_member?: boolean;
    options?: Record<string, any> | null;
  }) => updateMemberIntegration({
    workspace_id,
    id,
    is_active,
    is_default_for_member,
    options,
  }),
});

export const useViews = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["views", workspaceId],
    queryFn: () => getViews({
      workspace_identifier: workspaceId,
    }),
    select: (data: any) => data?.data?.views,
    staleTime: 1000 * 60 * 60 * 24,
    ...queryOptions,
  });

export const normalizeObjectSlug = (slug?: string) => {
  const normalized = String(slug || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "");
  if (!normalized) return "";
  if (normalized === "contacts") return "users";
  return normalized;
};

const inferObjectSlugFromFieldName = (fieldName?: string) => {
  const normalized = String(fieldName || "").trim().toLowerCase();
  if (normalized.startsWith("accounts.")) return "accounts";
  if (normalized.startsWith("users.") || normalized.startsWith("contacts.")) return "users";
  if (normalized.startsWith("companies.")) return "companies";
  if (normalized.startsWith("signal_runs.") || normalized.startsWith("sr.")) return "signal_runs";
  return "";
};

const getFieldBareName = (fieldName?: string) => {
  const normalized = String(fieldName || "").trim();
  if (!normalized) return "";
  const firstDot = normalized.indexOf(".");
  if (firstDot < 0) return normalized;
  return normalized.slice(firstDot + 1);
};

const buildWorkspaceObjectSchema = (data: any) => {
  const objects = Array.isArray(data?.objectsResponse?.data)
    ? data.objectsResponse.data
    : [];
  const fieldsAndMetrics = Array.isArray(data?.fieldsAndMetricsResponse)
    ? data.fieldsAndMetricsResponse
    : [];
  const relationshipsRaw = Array.isArray(data?.relationshipsResponse?.data)
    ? data.relationshipsResponse.data
    : [];

  const objectsBySlug = objects.reduce((acc: Record<string, any>, object: any) => {
    const slug = normalizeObjectSlug(object?.api_slug);
    if (slug) acc[slug] = object;
    return acc;
  }, {});

  const objectIdToSlug = objects.reduce((acc: Record<string, string>, object: any) => {
    const slug = normalizeObjectSlug(object?.api_slug);
    if (object?.id && slug) {
      acc[String(object.id)] = slug;
    }
    return acc;
  }, {});

  const fieldsByObjectSlug: Record<string, { list: any[]; obj: Record<string, any> }> = {};
  const metricsByObjectSlug: Record<string, { list: any[]; obj: Record<string, any> }> = {};
  const relationshipsByObjectSlug: Record<string, Array<{
    object_slug: string;
    relationship_label: string;
    cardinality: string;
    is_enabled: boolean;
    is_system: boolean;
  }>> = {};
  const allFields: any[] = [];
  const allMetrics: any[] = [];

  const ensureGroup = (groups: Record<string, { list: any[]; obj: Record<string, any> }>, slug: string) => {
    if (!groups[slug]) {
      groups[slug] = { list: [], obj: {} };
    }
    return groups[slug];
  };

  fieldsAndMetrics.forEach((item: any) => {
    const rawName = String(item?.name || item?.column_name || "").trim();
    if (!rawName) return;

    const objectSlug = normalizeObjectSlug(
      item?.object_slug
      || (item?.object_id ? objectIdToSlug[String(item.object_id)] : "")
      || inferObjectSlugFromFieldName(rawName),
    );
    const bareName = getFieldBareName(rawName);
    const isMetric = (item?.type === "metric" || item?.attribute_type === "metric_traits")
      && item?.generation_type !== "computed_trait";
    const shouldPrefix = !isMetric && !!objectSlug && rawName.indexOf(".") < 0;
    const qualifiedName = shouldPrefix ? `${objectSlug}.${bareName}` : rawName;

    const normalizedItem = {
      id: item?.id,
      name: qualifiedName,
      field_name: bareName,
      raw_name: rawName,
      object_slug: objectSlug || null,
      column_name: item?.column_name || rawName,
      display_name: item?.display_name || qualifiedName,
      data_type: item?.data_type || "string",
      source: item?.source || "native",
      attribute_type: item?.attribute_type || (isMetric ? "metric_traits" : "object_traits"),
      can_update: Boolean(item?.is_updateable),
      exclude_display: Boolean(item?.exclude_display),
      is_system: Boolean(item?.is_system),
      type: isMetric ? "metric" : "field",
      object_id: item?.object_id || null,
      generation_type: item?.generation_type || null,
      track_history: item?.track_history ?? null,
      display_format: item?.display_format || null,
    };

    if (isMetric) {
      allMetrics.push(normalizedItem);
      const metricGroupSlug = objectSlug || "global";
      const group = ensureGroup(metricsByObjectSlug, metricGroupSlug);
      group.list.push(normalizedItem);
      group.obj[normalizedItem.name] = normalizedItem;
      return;
    }

    allFields.push(normalizedItem);
    if (!objectSlug) return;
    const fieldGroup = ensureGroup(fieldsByObjectSlug, objectSlug);
    fieldGroup.list.push(normalizedItem);
    fieldGroup.obj[normalizedItem.name] = normalizedItem;
  });

  const relationshipSeen = new Set<string>();
  const normalizedRelationships = relationshipsRaw
    .map((item: any) => {
      const sourceSlug = normalizeObjectSlug(item?.source_object_slug);
      const destinationSlug = normalizeObjectSlug(item?.destination_object_slug);
      if (!sourceSlug || !destinationSlug) return null;
      return {
        id: item?.id,
        source_object_slug: sourceSlug,
        destination_object_slug: destinationSlug,
        relationship_label: item?.relationship_label || "associated_with",
        cardinality: item?.cardinality || "many_to_many",
        is_enabled: Boolean(item?.is_enabled),
        is_system: Boolean(item?.is_system),
        filterable_in_trend_search: Boolean(item?.filterable_in_trend_search),
      };
    })
    .filter(Boolean);

  const addRelationshipNeighbor = (
    fromSlug: string,
    toSlug: string,
    rel: {
      relationship_label: string;
      cardinality: string;
      is_enabled: boolean;
      is_system: boolean;
    },
  ) => {
    if (!relationshipsByObjectSlug[fromSlug]) {
      relationshipsByObjectSlug[fromSlug] = [];
    }

    const dedupeKey = [
      fromSlug,
      toSlug,
      rel.relationship_label,
      rel.cardinality,
      rel.is_enabled ? "1" : "0",
      rel.is_system ? "1" : "0",
    ].join("|");
    if (relationshipSeen.has(dedupeKey)) return;
    relationshipSeen.add(dedupeKey);

    relationshipsByObjectSlug[fromSlug].push({
      object_slug: toSlug,
      relationship_label: rel.relationship_label,
      cardinality: rel.cardinality,
      is_enabled: rel.is_enabled,
      is_system: rel.is_system,
    });
  };

  normalizedRelationships.forEach((rel: any) => {
    addRelationshipNeighbor(rel.source_object_slug, rel.destination_object_slug, rel);
    addRelationshipNeighbor(rel.destination_object_slug, rel.source_object_slug, rel);
  });

  Object.values(relationshipsByObjectSlug).forEach((neighbors) => {
    neighbors.sort((a, b) => a.object_slug.localeCompare(b.object_slug));
  });

  return {
    objects,
    objectsBySlug,
    fields: {
      list: allFields,
      obj: arrayToObject(allFields, "name"),
    },
    metrics: {
      list: allMetrics,
      obj: arrayToObject(allMetrics, "name"),
    },
    fieldsByObjectSlug,
    metricsByObjectSlug,
    relationships: normalizedRelationships,
    relationshipsByObjectSlug,
  };
};

const fetchWorkspaceSchemaPayload = async (workspaceId: string) => {
  const [objectsResponse, fieldsAndMetricsResponse, relationshipsResponse] = await Promise.all([
    getWorkspaceObjectsDetailed({ workspace_id: workspaceId }),
    getWorkspaceFieldsAndMetrics({ workspaceId }),
    getWorkspaceObjectRelationships({
      workspace_id: workspaceId,
      include_disabled: true,
      include_disabled_objects: true,
    }).catch(() => ({ data: [] })),
  ]);

  return {
    objectsResponse,
    fieldsAndMetricsResponse,
    relationshipsResponse,
  };
};

export const useWorkspaceObjectSchema = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["workspace-object-schema", workspaceId],
    queryFn: () => fetchWorkspaceSchemaPayload(workspaceId),
    select: (data: any) => buildWorkspaceObjectSchema(data),
    staleTime: 1000 * 60 * 60 * 24,
    ...queryOptions,
  });

export const useAccount = ({ workspaceId, accountId, ...queryOptions }:
  { workspaceId: string, accountId: string } & UseQueryOptions) => useQuery({
    queryKey: ["account", workspaceId, accountId],
    queryFn: () => getAccountById(
      { workspace_identifier: workspaceId, external_id: accountId },
    ),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useContact = ({ workspaceId, contactId, ...queryOptions }:
  { workspaceId: string, contactId: string } & UseQueryOptions) => useQuery({
    queryKey: ["contact", workspaceId, contactId],
    queryFn: () => getContactById(
      { workspace_identifier: workspaceId, external_id: contactId },
    ),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useAccountContacts = ({ workspaceId, accountId, ...queryOptions }:
  { workspaceId: string, accountId: string } & UseQueryOptions) => useQuery({
    queryKey: ["account_contacts", workspaceId, accountId],
    queryFn: () => getContactsByAccountId(
      { workspace_identifier: workspaceId, company_id: accountId },
    ),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useAllAutomations = ({ workspaceId, automationType, ...queryOptions }:
  { workspaceId: string, automationType: string } & UseQueryOptions) => useQuery({
    queryKey: ["all_automations", workspaceId, automationType],
    queryFn: () => readAllAutomations({ workspace_id: workspaceId, automation_type: automationType }),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useAllPlaybooks = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["all_playbooks", workspaceId],
    queryFn: () => readAllPlaybooks({ workspace_id: workspaceId }),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useAutomationLogs = ({
  workspaceId, automationId, page = 0, status, automationType, ...queryOptions
}:
  {
    workspaceId: string,
    automationType?: string,
    automationId?: string,
    status?: string,
    page?: number
  } & UseQueryOptions) => useQuery({
    queryKey: ["automation_logs", workspaceId, automationId, automationType, status, page],
    queryFn: () => (readAutomationLogs({
      workspace_id: workspaceId,
      automation_id: automationId || undefined,
      automation_type: automationType,
      status: status || undefined,
      skip: page * 20,
      limit: 20,
    })),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useInbox = ({
  workspaceId,
  automationId,
  timeStart,
  timeEnd,
  playbookId,
  signalStatus,
  notificationType,
  entityRelation,
  owner,
  ...queryOptions
}:
  {
    workspaceId: string,
    timeStart?: string,
    timeEnd?: string,
    playbookId?: string,
    automationId?: string,
    signalStatus?: "no_status" | "starred" | "dismissed" | "Done",
    notificationType: "inbox" | "notification",
    entityRelation?: "accounts" | "users",
    owner?: string[],
  } & UseQueryOptions) => useQuery({
    queryKey: ["inbox",
      workspaceId,
      automationId,
      entityRelation,
      timeStart,
      timeEnd,
      playbookId,
      signalStatus,
      notificationType,
      owner],
    queryFn: () => (readSignalRuns({
      workspace_id: workspaceId,
      entity_relation: entityRelation,
      automation_id: automationId || undefined,
      time_start: timeStart,
      time_end: timeEnd,
      playbook_id: playbookId || undefined,
      signal_status: signalStatus || undefined,
      notification_type: notificationType,
      owner_id: owner || undefined,
    })),
    select: (data: any) => data?.data?.results,
    staleTime: 1000 * 60 * 10,
    retryOnMount: false,
    ...queryOptions,
  });

export const useAllIntegrations = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["integrations", workspaceId],
    queryFn: () => getAllIntegration({ workspace_id: workspaceId }),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useSlackIntegration = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["slack-integration", workspaceId],
    queryFn: () => getSlackIntegration({ workspace_id: workspaceId }),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 10,
    retry: 0,
    ...queryOptions,
  });

export const useSlackUsers = () => useMutation({
  mutationFn: ({ workspace_id, refresh = false }: { workspace_id: string, refresh?: boolean }) =>
    getSlackUsers({ workspace_id, refresh }),
});

export const useSlackChannels = () => useMutation({
  mutationFn: ({ workspace_id, refresh = false }: { workspace_id: string, refresh?: boolean }) =>
    getSlackChannels({ workspace_id, refresh }),
});

export const useCreateExport = () => useMutation({
  mutationFn: ({ workspace_id, view_id }: { workspace_id: string; view_id: string }) =>
    createExport({ workspace_id, view_id }),
});

export const useListExports = ({ workspaceId, skip, limit, ...queryOptions }:
  { workspaceId: string; skip?: number; limit?: number } & UseQueryOptions) => useQuery({
    queryKey: ["exports", workspaceId, skip, limit],
    queryFn: () => listExports({
      workspace_id: workspaceId,
      skip,
      limit,
    }),
    select: (data: any) => data?.data as ListExportsResponse,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    ...queryOptions,
  });

export const useGetExport = ({ workspaceId, id, ...queryOptions }:
  { workspaceId: string; id: string } & UseQueryOptions) => useQuery({
    queryKey: ["export", workspaceId, id],
    queryFn: () => getExport({
      workspace_id: workspaceId,
      id,
    }),
    select: (data: any) => data?.data as ExportResponse,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    ...queryOptions,
  });

export const useDownloadExport = () => useMutation({
  mutationFn: ({ workspace_id, id }: { workspace_id: string; id: string }) =>
    downloadExport({ workspace_id, id }),
});

export const useSignalAutomations = ({
  workspaceId,
  automationId,
  status,
  playbookId,
  ...queryOptions
}:
  {
    workspaceId: string,
    automationId?: string,
    status?: string,
    playbookId?: string
  } & UseQueryOptions) => useQuery({
    queryKey: ["signal_automations", workspaceId, automationId, status, playbookId],
    queryFn: () => readSignalAutomations({
      workspace_id: workspaceId,
      automation_id: automationId,
      status,
      playbook_id: playbookId,
    }),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useIntegrationAppToken = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["integration-app-token", workspaceId],
    queryFn: () => generateIntegrationAppToken({ workspace_id: workspaceId }),
    select: (data: any) => data?.data,
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
    ...queryOptions,
  });

export const useVerifyIntegrationAppToken = ({ token, ...queryOptions }:
  { token: string } & UseQueryOptions) => useQuery({
    queryKey: ["verify-integration-app-token", token],
    queryFn: () => verifyIntegrationAppToken({ token }),
    select: (data: any) => data?.data,
    enabled: !!token,
    ...queryOptions,
  });

export const useTestIntegrationAppConnection = ({ ...queryOptions }: UseQueryOptions = {}) => useQuery({
  queryKey: ["integration-app-connection"],
  queryFn: () => testIntegrationAppConnection(),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  ...queryOptions,
});

export const useWorkspaceFields = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["workspace-fields", workspaceId],
    queryFn: () => getWorkspaceFields({ workspace_id: workspaceId }),
    select: (data: any) => data?.data,
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    ...queryOptions,
  });

export const useObjectFields = ({ workspaceId, objectId, ...queryOptions }:
  { workspaceId: string; objectId: string } & UseQueryOptions) => useQuery({
    queryKey: ["object-fields", workspaceId, objectId],
    queryFn: () => getObjectFields({ workspace_id: workspaceId, objectId }),
    select: (data: any) => data?.data,
    enabled: !!workspaceId && !!objectId,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    ...queryOptions,
  });

export const useSyncObjectFields = ({ workspaceId, objectSlug, ...queryOptions }:
  { workspaceId: string; objectSlug: string } & UseQueryOptions) => useQuery({
    queryKey: ["sync-object-fields", workspaceId, objectSlug],
    queryFn: () => getSyncObjectFields({ workspace_id: workspaceId, object_slug: objectSlug }),
    enabled: !!workspaceId && !!objectSlug,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useFieldMappings = ({ workspaceId, syncId, ...queryOptions }:
  { workspaceId: string; syncId: string } & UseQueryOptions) => useQuery({
    queryKey: ["field-mappings", workspaceId, syncId],
    queryFn: () => getFieldMappings({ workspace_id: workspaceId, sync_id: syncId }),
    select: (data: any) => (Array.isArray(data?.data) ? data.data : data),
    enabled: !!workspaceId && !!syncId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

// Sync Configuration Hooks
export const useHubspotIntegration = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["hubspot-integration", workspaceId],
    queryFn: async () => {
      const response = await getAllIntegration({ workspace_id: workspaceId });
      return response?.data?.filter((item: any) => item?.source_name === "hubspot");
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useSalesforceIntegration = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["salesforce-integration", workspaceId],
    queryFn: async () => {
      const response = await getAllIntegration({ workspace_id: workspaceId });
      return response?.data?.filter((item: any) => item?.source_name === "salesforce");
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useGmailIntegration = ({ workspaceId, userId, ...queryOptions }:
  { workspaceId: string; userId?: string } & UseQueryOptions) => useQuery({
    queryKey: ["gmail-integration", workspaceId, userId],
    queryFn: async () => {
      const response = await getAllIntegration({ workspace_id: workspaceId });
      const integrations = response?.data?.filter((item: any) => item?.source_name === "gmail") ?? [];

      // New model: Gmail is a top-level workspace integration with nested `member_integrations`.
      const topLevel = integrations.filter((integration: any) => {
        const isMemberLevel = Boolean(
          integration?.is_member_level ?? integration?.isMemberLevel ?? integration?.member_level ?? integration?.memberLevel,
        );
        return !isMemberLevel;
      });
      if (topLevel.length > 0) return topLevel;

      // Backwards compatibility: older member-level integrations were scoped by external_id = "{workspace_id}:{user_id}".
      if (!userId) return integrations;

      const expectedExternalId = `${workspaceId}:${userId}`;
      return integrations.filter((integration: any) => {
        const externalId = integration?.external_id ?? integration?.externalId;
        if (externalId) return externalId === expectedExternalId;

        const integrationUserId = integration?.user_id ?? integration?.userId ?? integration?.created_by_user_id ?? integration?.createdByUserId;
        return integrationUserId === userId;
      });
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useMeetingNoteTakerPreference = ({ workspaceId, workspaceMemberId, ...queryOptions }:
  { workspaceId: string, workspaceMemberId?: string | null } & UseQueryOptions) => useQuery({
    queryKey: ["meeting-note-taker-preference", workspaceId, workspaceMemberId],
    queryFn: async () => {
      try {
        return await getMeetingNoteTakerPreference({
          workspace_id: workspaceId,
          workspace_member_id: workspaceMemberId,
        });
      } catch (error: any) {
        if (error.response && error.response.status === 404) {
          return null;
        }
        throw error;
      }
    },
    select: (data: any) => data?.data as MeetingNoteTakerPreference,
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useUpsertMeetingNoteTakerPreference = () => useMutation({
  mutationFn: ({ workspace_id, workspace_member_id, data }: {
    workspace_id: string;
    workspace_member_id?: string | null;
    data: MeetingNoteTakerPreferenceUpdate;
  }) => upsertMeetingNoteTakerPreference({
    workspace_id,
    workspace_member_id,
    data,
  }),
});

export const useDeleteMeetingNoteTakerPreference = () => useMutation({
  mutationFn: ({ workspace_id, workspace_member_id }: {
    workspace_id: string;
    workspace_member_id: string;
  }) => deleteMeetingNoteTakerPreference({
    workspace_id,
    workspace_member_id,
  }),
});

export const useGoogleCalendarIntegration = ({ workspaceId, userId, ...queryOptions }:
  { workspaceId: string; userId?: string } & UseQueryOptions) => useQuery({
    queryKey: ["google-calendar-integration", workspaceId, userId],
    queryFn: async () => {
      const response = await getAllIntegration({ workspace_id: workspaceId });
      const integrations = response?.data?.filter((item: any) => item?.source_name === "google-calendar") ?? [];

      // New model: Google Calendar is a top-level workspace integration with nested `member_integrations`.
      const topLevel = integrations.filter((integration: any) => {
        const isMemberLevel = Boolean(
          integration?.is_member_level ?? integration?.isMemberLevel ?? integration?.member_level ?? integration?.memberLevel,
        );
        return !isMemberLevel;
      });
      if (topLevel.length > 0) return topLevel;

      // Backwards compatibility: older member-level integrations were scoped by external_id = "{workspace_id}:{user_id}".
      if (!userId) return integrations;

      const expectedExternalId = `${workspaceId}:${userId}`;
      return integrations.filter((integration: any) => {
        const externalId = integration?.external_id ?? integration?.externalId;
        if (externalId) return externalId === expectedExternalId;

        const integrationUserId = integration?.user_id ?? integration?.userId ?? integration?.created_by_user_id ?? integration?.createdByUserId;
        return integrationUserId === userId;
      });
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 10,
    ...queryOptions,
  });

export const useSyncConfigurations = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["sync-configurations", workspaceId],
    queryFn: () => getSyncConfigurations({ workspace_id: workspaceId }),
    enabled: !!workspaceId,
    staleTime: 1000 * 60,
    ...queryOptions,
  });

export const useAvailableObjects = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["available-objects", workspaceId],
    queryFn: () => getAvailableObjects({ workspace_id: workspaceId }),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useFieldsAndMetrics = ({ workspaceId, objectSlug, ...queryOptions }:
  { workspaceId: string; objectSlug: string } & UseQueryOptions) => useQuery({
    queryKey: ["fields-and-metrics", workspaceId, objectSlug],
    queryFn: () => getFieldsAndMetrics({ workspace_id: workspaceId, object_slug: objectSlug }),
    select: (data: any) => data?.data,
    enabled: !!workspaceId && !!objectSlug,
    staleTime: 1000 * 60,
    ...queryOptions,
  });

export const useWorkspaceObjects = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions<GetWorkspaceObjectsResponse, Error>) => useQuery<GetWorkspaceObjectsResponse, Error>({
    queryKey: ["workspace-objects", workspaceId],
    queryFn: () => getWorkspaceObjects({ workspace_id: workspaceId }),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

// AI Memory hooks
export const useAIMemoryObjects = ({ workspaceId, ...queryOptions }:
  { workspaceId?: string } & UseQueryOptions<ListAIMemoryObjectsResponse, Error>) => useQuery<ListAIMemoryObjectsResponse, Error>({
    queryKey: ["ai-memory-objects", workspaceId],
    queryFn: () => listAIMemoryObjects({ workspace_id: workspaceId as string }),
    enabled: !!workspaceId,
    staleTime: 1000 * 60,
    ...queryOptions,
  });

export const useEnableAIMemoryObject = () => useMutation({
  mutationFn: ({ workspace_id, object_type }: { workspace_id: string; object_type: string }) =>
    enableAIMemoryObject({ workspace_id, object_type } as any),
});

export const useAIMemoryFieldDefinitions = ({ workspaceId, objectId, enabled, ...queryOptions }:
  { workspaceId?: string; objectId?: string; enabled?: boolean } & UseQueryOptions<GetFieldDefinitionsResponse, Error>) => useQuery<GetFieldDefinitionsResponse, Error>({
    queryKey: ["ai-memory-field-definitions", workspaceId, objectId],
    queryFn: () => getAIMemoryFieldDefinitions({ workspace_id: workspaceId as string, object_id: objectId as string }),
    enabled: !!workspaceId && !!objectId && !!enabled,
    staleTime: 1000 * 30,
    ...queryOptions,
  });

export const useUpdateAIMemoryFieldDefinition = () => useMutation({
  mutationFn: ({ workspace_id, field_id, data }: { workspace_id: string; field_id: string; data: any }) =>
    updateAIMemoryFieldDefinition({ workspace_id, field_id, data }),
});

// Data Management hooks
export const useWorkspaceObjectsDetailed = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["workspace-objects-detailed", workspaceId],
    queryFn: () => getWorkspaceObjectsDetailed({ workspace_id: workspaceId }),
    select: (data: any) => data?.data,
    enabled: !!workspaceId,
    staleTime: 1000 * 60,
    ...queryOptions,
  });

export const useWorkspaceObjectRelationships = ({
  workspaceId,
  includeDisabled = true,
  includeDisabledObjects = true,
  ...queryOptions
}: {
  workspaceId: string;
  includeDisabled?: boolean;
  includeDisabledObjects?: boolean;
} & UseQueryOptions) => useQuery({
  queryKey: [
    "workspace-object-relationships",
    workspaceId,
    includeDisabled,
    includeDisabledObjects,
  ],
  queryFn: () => getWorkspaceObjectRelationships({
    workspace_id: workspaceId,
    include_disabled: includeDisabled,
    include_disabled_objects: includeDisabledObjects,
  }),
  select: (data: any) => data?.data || [],
  enabled: !!workspaceId,
  staleTime: 1000 * 60,
  ...queryOptions,
});

export const useUpdateWorkspaceObject = () => useMutation({
  mutationFn: ({ workspace_id, object_id, data }: {
    workspace_id: string;
    object_id: string;
    data: any
  }) => updateWorkspaceObject({ workspace_id, object_id, data }),
});

export const useCreateWorkspaceObject = () => useMutation({
  mutationFn: ({ workspace_id, data }: {
    workspace_id: string;
    data: any
  }) => createWorkspaceObject({ workspace_id, data }),
});

export const useWorkspaceFieldsAPI = ({ workspaceId, ...queryOptions }:
  { workspaceId: string } & UseQueryOptions) => useQuery({
    queryKey: ["workspace-fields-api", workspaceId],
    queryFn: () => getWorkspaceFieldsAPI({ workspace_id: workspaceId }),
    select: (data: any) => data?.data,
    enabled: !!workspaceId,
    staleTime: 1000 * 60,
    ...queryOptions,
  });

export const useObjectFieldsAPI = ({ workspaceId, objectId, ...queryOptions }:
  { workspaceId: string; objectId: string } & UseQueryOptions) => useQuery({
    queryKey: ["object-fields-api", workspaceId, objectId],
    queryFn: () => getObjectFieldsAPI({ workspace_id: workspaceId, object_id: objectId }),
    enabled: !!workspaceId && !!objectId,
    select: (data: any) => data?.data,
    staleTime: 1000 * 60,
    ...queryOptions,
  });

export const useWorkspaceObjectWithFields = ({ workspaceId, objectId, ...queryOptions }:
  { workspaceId: string; objectId: string } & UseQueryOptions) => useQuery({
    queryKey: ["workspace-object-with-fields", workspaceId, objectId],
    queryFn: () => getWorkspaceObjectWithFields({ workspace_id: workspaceId, object_id: objectId }),
    enabled: !!workspaceId && !!objectId,
    staleTime: 1000 * 60,
    ...queryOptions,
  });

export const useCreateObjectField = () => useMutation({
  mutationFn: ({ workspace_id, object_id, data }: {
    workspace_id: string;
    object_id: string;
    data: any
  }) => createObjectField({ workspace_id, object_id, data }),
});

export const useCreateBulkObjectFields = () => useMutation({
  mutationFn: ({ workspace_id, object_id, data }: {
    workspace_id: string;
    object_id: string;
    data: any[]
  }) => createBulkObjectFields({ workspace_id, object_id, data }),
});

export const useUpdateWorkspaceField = () => useMutation({
  mutationFn: ({ workspace_id, field_id, data }: {
    workspace_id: string;
    field_id: string;
    data: any
  }) => updateWorkspaceField({ workspace_id, field_id, data }),
});

export const useCreateSyncConfiguration = () => useMutation({
  mutationFn: ({ workspace_id, data }: {
    workspace_id: string;
    data: any
  }) => createSyncConfiguration({ workspace_id, data }),
  onSuccess: (response: any, variables) => {
    const created = response?.data ?? response;
    if (!created) return;

    queryClient.setQueryData(
      ["sync-configurations", variables.workspace_id],
      (oldData: any) => {
        if (!oldData) return Array.isArray(created) ? created : [created];

        if (Array.isArray(oldData)) {
          const alreadyPresent = oldData.some((sync) => sync?.id && sync.id === created?.id);
          if (alreadyPresent) return oldData;
          return [...oldData, created];
        }

        if (oldData?.data && Array.isArray(oldData.data)) {
          const alreadyPresent = oldData.data.some((sync: any) => sync?.id && sync.id === created?.id);
          if (alreadyPresent) return oldData;
          return {
            ...oldData,
            data: [...oldData.data, created],
          };
        }

        return oldData;
      },
    );
  }
});

export const useCreateWriteBackSync = () => useMutation({
  mutationFn: ({ workspace_id, original_sync, create_missing_records }: {
    workspace_id: string;
    original_sync: any;
    create_missing_records?: boolean;
  }) => createWriteBackSync({ workspace_id, original_sync, create_missing_records }),
  onSuccess: (response: any, variables) => {
    const created = response?.data ?? response;
    if (!created) return;

    queryClient.setQueryData(
      ["sync-configurations", variables.workspace_id],
      (oldData: any) => {
        if (!oldData) return Array.isArray(created) ? created : [created];

        if (Array.isArray(oldData)) {
          const alreadyPresent = oldData.some((sync) => sync?.id && sync.id === created?.id);
          if (alreadyPresent) return oldData;
          return [...oldData, created];
        }

        if (oldData?.data && Array.isArray(oldData.data)) {
          const alreadyPresent = oldData.data.some((sync: any) => sync?.id && sync.id === created?.id);
          if (alreadyPresent) return oldData;
          return {
            ...oldData,
            data: [...oldData.data, created],
          };
        }

        return oldData;
      },
    );
  }
});

export const useUpdateSyncConfiguration = () => useMutation({
  mutationFn: ({ workspace_id, sync_id, data }: {
    workspace_id: string;
    sync_id: string;
    data: any
  }) => updateSyncConfiguration({ workspace_id, sync_id, data }),
  onSuccess: (updatedSync, variables) => {
    // Optimistically update the sync configurations query cache with the response data
    queryClient.setQueryData(
      ["sync-configurations", variables.workspace_id],
      (oldData: any[] | undefined) => {
        if (!oldData) return oldData;
        const normalized = (updatedSync && (updatedSync as any).data) ? (updatedSync as any).data : updatedSync;
        // Find and replace the updated sync configuration
        return oldData.map(sync =>
          sync && sync.id === variables.sync_id
            ? { ...sync, ...normalized }
            : sync
        );
      }
    );
  }
});

export const useDeleteSyncConfiguration = () => useMutation({
  mutationFn: ({ workspace_id, sync_id }: {
    workspace_id: string;
    sync_id: string;
  }) => deleteSyncConfiguration({ workspace_id, sync_id }),
  onSuccess: (_, variables) => {
    // Remove the deleted sync configuration from the query cache
    queryClient.setQueryData(
      ["sync-configurations", variables.workspace_id],
      (oldData: any) => {
        if (!oldData) return oldData;

        if (Array.isArray(oldData)) {
          return oldData.filter((sync) => sync?.id !== variables.sync_id);
        }

        if (oldData?.data && Array.isArray(oldData.data)) {
          return {
            ...oldData,
            data: oldData.data.filter((sync: any) => sync?.id !== variables.sync_id),
          };
        }

        return oldData;
      }
    );
  }
});

// Field Mapping Mutation Hooks
export const useCreateBulkFieldMappings = () => useMutation({
  mutationFn: ({ workspace_id, sync_id, mappings }: {
    workspace_id: string;
    sync_id: string;
    mappings: any[];
  }) => createBulkFieldMappings({ workspace_id, sync_id, mappings }),
  onSuccess: (_, variables) => {
    // Invalidate field mappings query to refresh data
    queryClient.invalidateQueries({
      queryKey: ["field-mappings", variables.workspace_id, variables.sync_id]
    });
  }
});

export const useUpdateFieldMapping = () => useMutation({
  mutationFn: ({ workspace_id, sync_id, mapping_id, data }: {
    workspace_id: string;
    sync_id: string;
    mapping_id: string;
    data: any;
  }) => updateFieldMapping({ workspace_id, sync_id, mapping_id, data }),
  onSuccess: (_, variables) => {
    // Invalidate field mappings query to refresh data
    queryClient.invalidateQueries({
      queryKey: ["field-mappings", variables.workspace_id, variables.sync_id]
    });
  }
});

export const useDeleteFieldMapping = () => useMutation({
  mutationFn: ({ workspace_id, sync_id, mapping_id }: {
    workspace_id: string;
    sync_id: string;
    mapping_id: string;
  }) => deleteFieldMapping({ workspace_id, sync_id, mapping_id }),
  onSuccess: (_, variables) => {
    // Invalidate field mappings query to refresh data
    queryClient.invalidateQueries({
      queryKey: ["field-mappings", variables.workspace_id, variables.sync_id]
    });
  }
});

export const useCreateBulkFields = () => useMutation({
  mutationFn: ({ workspace_id, objectId, fields, objectSlug }: {
    workspace_id: string;
    objectId: string;
    fields: any[];
    objectSlug: string;
  }) => createBulkFields({ workspace_id, objectId, fields }),
  onSuccess: (response, variables) => {
    // Optimistically update cache with created fields instead of invalidating
    const createdFields = response?.data || [];
    const { objectSlug } = variables;

    if (createdFields.length > 0) {
      // Update sync-object-fields cache for the specific object slug
      queryClient.setQueryData(
        ["sync-object-fields", variables.workspace_id, objectSlug],
        (oldData: any) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: [...oldData.data, ...createdFields]
          };
        }
      );

      // Update object-fields cache
      queryClient.setQueryData(
        ["object-fields", variables.workspace_id, variables.objectId],
        (oldData: any) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: [...oldData.data, ...createdFields]
          };
        }
      );

      // Update workspace-fields cache
      queryClient.setQueryData(
        ["workspace-fields", variables.workspace_id],
        (oldData: any) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: [...oldData.data, ...createdFields]
          };
        }
      );
    }
  }
});

// Note: Metrics configuration hooks are disabled until API endpoints are available
// These will be implemented when the backend provides the metrics endpoints

export const useRecommendedSignals = ({
  workspaceId,
  entityType,
  limit = 10,
  refreshCounter = 0,
  ...queryOptions
}: {
  workspaceId?: string;
  entityType?: "accounts" | "users";
  limit?: number;
  refreshCounter?: number;
} & UseQueryOptions) => {
  const cacheKey = `recommended-signals-${workspaceId}-${entityType || "all"}-${limit}`;

  // Check localStorage cache (no expiration - cache persists until manually cleared)
  const getCachedData = (): { data: RecommendedSignalsResponse; timestamp: number } | null => {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const parsed = JSON.parse(cached) as { data: RecommendedSignalsResponse; timestamp: number };
      return parsed;
    } catch {
      return null;
    }
  };

  // Save to localStorage
  const saveToCache = (data: RecommendedSignalsResponse) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch {
      // Ignore localStorage errors
    }
  };

  return useQuery({
    queryKey: ["recommended-signals", workspaceId, entityType, limit, refreshCounter],
    queryFn: async () => {
      // Skip cache check if refreshCounter > 0 (user manually refreshed)
      if (refreshCounter === 0) {
        const cached = getCachedData();
        if (cached) {
          return cached.data;
        }
      }

      // Fetch from API
      const data = await getRecommendedSignals({
        workspaceId: workspaceId!,
        entityType,
        limit,
      });

      // Save to cache
      saveToCache(data);

      return data;
    },
    enabled: !!workspaceId,
    staleTime: Infinity, // Cache indefinitely until manually cleared
    retry: 2,
    ...queryOptions,
  });
};

// Associated Objects (Emails & Meetings) hooks
export const useAccountAssociatedObjects = ({
  workspaceId,
  accountId,
  objectTypes,
  limit,
  offset,
  ...queryOptions
}: {
  workspaceId: string;
  accountId: string;
  objectTypes?: string[];
  limit?: number;
  offset?: number;
} & UseQueryOptions<AssociatedObjectsResponse, Error>) =>
  useQuery<AssociatedObjectsResponse, Error>({
    queryKey: ["account-associated-objects", workspaceId, accountId, objectTypes, limit, offset],
    queryFn: () =>
      getAccountAssociatedObjects({
        workspace_id: workspaceId,
        account_external_id: accountId,
        object_types: objectTypes,
        limit,
        offset,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!accountId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    ...queryOptions,
  });

export const useAccountEmails = ({
  workspaceId,
  accountId,
  limit,
  offset,
  ...queryOptions
}: {
  workspaceId: string;
  accountId: string;
  limit?: number;
  offset?: number;
} & UseQueryOptions<EmailsResponse, Error>) =>
  useQuery<EmailsResponse, Error>({
    queryKey: ["account-emails", workspaceId, accountId, limit, offset],
    queryFn: () =>
      getAccountEmails({
        workspace_id: workspaceId,
        account_external_id: accountId,
        limit,
        offset,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!accountId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    ...queryOptions,
  });

export const useAccountMeetings = ({
  workspaceId,
  accountId,
  limit,
  offset,
  ...queryOptions
}: {
  workspaceId: string;
  accountId: string;
  limit?: number;
  offset?: number;
} & UseQueryOptions<MeetingsResponse, Error>) =>
  useQuery<MeetingsResponse, Error>({
    queryKey: ["account-meetings", workspaceId, accountId, limit, offset],
    queryFn: () =>
      getAccountMeetings({
        workspace_id: workspaceId,
        account_external_id: accountId,
        limit,
        offset,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!accountId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    ...queryOptions,
  });

export const useContactAssociatedObjects = ({
  workspaceId,
  contactId,
  objectTypes,
  limit,
  offset,
  ...queryOptions
}: {
  workspaceId: string;
  contactId: string;
  objectTypes?: string[];
  limit?: number;
  offset?: number;
} & UseQueryOptions<AssociatedObjectsResponse, Error>) =>
  useQuery<AssociatedObjectsResponse, Error>({
    queryKey: ["contact-associated-objects", workspaceId, contactId, objectTypes, limit, offset],
    queryFn: () =>
      getContactAssociatedObjects({
        workspace_id: workspaceId,
        contact_external_id: contactId,
        object_types: objectTypes,
        limit,
        offset,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!contactId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    ...queryOptions,
  });

export const useContactEmails = ({
  workspaceId,
  contactId,
  limit,
  offset,
  ...queryOptions
}: {
  workspaceId: string;
  contactId: string;
  limit?: number;
  offset?: number;
} & UseQueryOptions<EmailsResponse, Error>) =>
  useQuery<EmailsResponse, Error>({
    queryKey: ["contact-emails", workspaceId, contactId, limit, offset],
    queryFn: () =>
      getContactEmails({
        workspace_id: workspaceId,
        contact_external_id: contactId,
        limit,
        offset,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!contactId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    ...queryOptions,
  });

export const useContactMeetings = ({
  workspaceId,
  contactId,
  limit,
  offset,
  ...queryOptions
}: {
  workspaceId: string;
  contactId: string;
  limit?: number;
  offset?: number;
} & UseQueryOptions<MeetingsResponse, Error>) =>
  useQuery<MeetingsResponse, Error>({
    queryKey: ["contact-meetings", workspaceId, contactId, limit, offset],
    queryFn: () =>
      getContactMeetings({
        workspace_id: workspaceId,
        contact_external_id: contactId,
        limit,
        offset,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!contactId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    ...queryOptions,
  });

export const useTraitHistory = ({
  workspaceId,
  entityType,
  recordId,
  traitName,
  ...queryOptions
}: {
  workspaceId: string;
  entityType: string;
  recordId: string;
  traitName: string;
} & UseQueryOptions<TraitHistoryResponse, Error>) =>
  useQuery<TraitHistoryResponse, Error>({
    queryKey: ["trait-history", workspaceId, entityType, recordId, traitName],
    queryFn: () =>
      getTraitHistory({
        workspace_id: workspaceId,
        entity_type: entityType,
        record_id: recordId,
        trait_name: traitName,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!entityType && !!recordId && !!traitName,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useTraitHistoryAll = ({
  workspaceId,
  entityType,
  recordId,
  ...queryOptions
}: {
  workspaceId: string;
  entityType: string;
  recordId: string;
} & UseQueryOptions<TraitHistoryAllResponse, Error>) =>
  useQuery<TraitHistoryAllResponse, Error>({
    queryKey: ["trait-history-all", workspaceId, entityType, recordId],
    queryFn: () =>
      getTraitHistoryAll({
        workspace_id: workspaceId,
        entity_type: entityType,
        record_id: recordId,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!entityType && !!recordId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

// ---- Object field distinct values (for filter dropdowns) ----

export const useObjectFieldValues = ({
  workspaceId,
  objectType,
  field,
  search,
  limit,
  enabled,
}: {
  workspaceId: string;
  objectType: string;
  field: string;
  search?: string;
  limit?: number;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: ["objectFieldValues", workspaceId, objectType, field, search],
    queryFn: () =>
      getObjectFieldValues({
        workspace_id: workspaceId,
        object_type: objectType,
        field,
        search,
        limit,
      }).then((res) => res.data),
    select: (data) => data?.values || [],
    staleTime: 1000 * 60 * 5,
    enabled: !!workspaceId && !!objectType && !!field && enabled !== false,
  });

// ---- Skills ----

export const useWorkspaceSkills = ({
  workspaceId,
  ...queryOptions
}: {
  workspaceId?: string;
} & UseQueryOptions) =>
  useQuery({
    queryKey: ["skills", workspaceId],
    queryFn: () =>
      getWorkspaceSkills({ workspace_id: workspaceId! }).then(
        (res) => res.data,
      ),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useSkill = ({
  workspaceId,
  skillId,
  ...queryOptions
}: {
  workspaceId?: string;
  skillId?: string;
} & UseQueryOptions) =>
  useQuery({
    queryKey: ["skill", workspaceId, skillId],
    queryFn: () =>
      getSkill({
        workspace_id: workspaceId!,
        skill_id: skillId!,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!skillId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useCreateSkill = () =>
  useMutation({
    mutationFn: (data: CreateSkillPayload) => createSkill(data),
  });

export const useUpdateSkill = () =>
  useMutation({
    mutationFn: (data: UpdateSkillPayload) => updateSkill(data),
  });

export const useDeleteSkill = () =>
  useMutation({
    mutationFn: ({
      workspace_id,
      skill_id,
    }: {
      workspace_id: string;
      skill_id: string;
    }) => deleteSkill({ workspace_id, skill_id }),
  });

export const useToggleSkill = () =>
  useMutation({
    mutationFn: ({
      workspace_id,
      skill_id,
      enabled,
    }: {
      workspace_id: string;
      skill_id: string;
      enabled: boolean;
    }) => toggleSkill({ workspace_id, skill_id, enabled }),
  });

export const useSkillVersions = ({
  workspaceId,
  skillId,
  ...queryOptions
}: {
  workspaceId?: string;
  skillId?: string;
} & UseQueryOptions) =>
  useQuery({
    queryKey: ["skillVersions", workspaceId, skillId],
    queryFn: () =>
      getSkillVersions({
        workspace_id: workspaceId!,
        skill_id: skillId!,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!skillId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useGenerateSkillContent = () =>
  useMutation({
    mutationFn: (data: GenerateSkillContentPayload) =>
      generateSkillContent(data).then((res) => res.data),
  });

// ---- Signal Documents ----

export const useSignalDocument = ({
  workspaceId,
  documentId,
  ...queryOptions
}: {
  workspaceId?: string;
  documentId?: string;
} & UseQueryOptions) =>
  useQuery({
    queryKey: ["signal-document", workspaceId, documentId],
    queryFn: () =>
      getSignalDocument({
        workspace_id: workspaceId!,
        document_id: documentId!,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!documentId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useSignalDocuments = ({
  workspaceId,
  entityId,
  signalRunId,
  automationId,
  healthModelKey,
  signalType,
  ...queryOptions
}: {
  workspaceId?: string;
  entityId?: string;
  signalRunId?: string;
  automationId?: string;
  healthModelKey?: string;
  signalType?: string;
} & UseQueryOptions) =>
  useQuery({
    queryKey: ["signal-documents", workspaceId, entityId, signalRunId, automationId, healthModelKey, signalType],
    queryFn: () =>
      listSignalDocuments({
        workspace_id: workspaceId!,
        entity_id: entityId,
        signal_run_id: signalRunId,
        automation_id: automationId,
        health_model_key: healthModelKey,
        signal_type: signalType,
      }).then((res) => res.data.documents),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 2,
    ...queryOptions,
  });

export const useDocumentVersions = ({
  workspaceId,
  documentId,
  ...queryOptions
}: {
  workspaceId?: string;
  documentId?: string;
} & UseQueryOptions) =>
  useQuery({
    queryKey: ["signal-document-versions", workspaceId, documentId],
    queryFn: () =>
      getDocumentVersions({
        workspace_id: workspaceId!,
        document_id: documentId!,
      }).then((res) => res.data.documents),
    enabled: !!workspaceId && !!documentId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useSubmitDocumentFeedback = () =>
  useMutation({
    mutationFn: (data: SubmitFeedbackPayload) =>
      submitDocumentFeedback(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(["signal-documents"]);
    },
  });

export const useDocumentFeedback = ({
  workspaceId,
  documentId,
  ...queryOptions
}: {
  workspaceId?: string;
  documentId?: string;
} & UseQueryOptions) =>
  useQuery({
    queryKey: ["signal-document-feedback", workspaceId, documentId],
    queryFn: () =>
      listDocumentFeedback({
        workspace_id: workspaceId!,
        document_id: documentId!,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!documentId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useHealthScoreSettings = ({
  workspaceId,
  ...queryOptions
}: {
  workspaceId?: string;
} & UseQueryOptions) =>
  useQuery({
    queryKey: ["health-score-settings", workspaceId],
    queryFn: () =>
      readHealthScoreSettings({ workspace_id: workspaceId! }).then((res) => res.data),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useSkillEffectiveness = ({
  workspaceId,
  skillId,
  ...queryOptions
}: {
  workspaceId?: string;
  skillId?: string;
} & UseQueryOptions) =>
  useQuery({
    queryKey: ["skill-effectiveness", workspaceId, skillId],
    queryFn: () =>
      getSkillEffectiveness({
        workspace_id: workspaceId!,
        skill_id: skillId!,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!skillId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

// Email-first onboarding hooks
export const useEmailSettings = ({ workspaceId, ...queryOptions }:
  { workspaceId?: string } & UseQueryOptions) => useQuery({
    queryKey: ["email-settings", workspaceId],
    queryFn: () => getEmailSettings({ workspace_id: workspaceId as string }),
    select: (data: any) => data?.data as EmailSettings,
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useUpdateEmailSettings = () => useMutation({
  mutationFn: (payload: UpdateEmailSettings) => updateEmailSettings(payload),
  onSuccess: (_, variables) => {
    queryClient.invalidateQueries({ queryKey: ["email-settings", variables.workspace_id] });
    toast.success("Email settings saved.");
  },
});

export const useComputedLabels = ({ workspaceId, ...queryOptions }:
  { workspaceId?: string } & UseQueryOptions) => useQuery({
    queryKey: ["computed-labels", workspaceId],
    queryFn: () => listComputedLabels({ workspace_id: workspaceId as string }),
    select: (data: any) => (data?.data || []) as ComputedLabel[],
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useEmailDomainReview = ({ workspaceId, status, ...queryOptions }:
  { workspaceId?: string; status?: EmailDomainReviewStatus } & UseQueryOptions) => useQuery({
    queryKey: ["email-domain-review", workspaceId, status],
    queryFn: () => getEmailDomainReview({ workspace_id: workspaceId as string, status }),
    select: (data: any) => data?.data as EmailDomainReviewList,
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
  });

export const useAllowEmailDomain = () => useMutation({
  mutationFn: ({ workspace_id, domain, note }: { workspace_id: string; domain: string; note?: string }) =>
    allowEmailDomain({ workspace_id, domain, note }),
  onSuccess: (_, variables) => {
    queryClient.invalidateQueries({ queryKey: ["email-domain-review", variables.workspace_id] });
    toast.success("Domain allowed.");
  },
});

export const useBlockEmailDomain = () => useMutation({
  mutationFn: ({ workspace_id, domain, note }: { workspace_id: string; domain: string; note?: string }) =>
    blockEmailDomain({ workspace_id, domain, note }),
  onSuccess: (_, variables) => {
    queryClient.invalidateQueries({ queryKey: ["email-domain-review", variables.workspace_id] });
    toast.success("Domain blocked.");
  },
});
